import { cloneDeep, isEqual } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { zodResolver } from '@hookform/resolvers/zod';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { OnCloseType } from '@semcore/ui/side-panel';

import {
  contentApi,
  useCreateVideoContentMutation,
  useGetVideoContentQuery,
  usePublishContentMutation,
  useUnpublishContentMutation,
  useUpdateVideoContentMutation,
} from '@/entities/content/api/contentApi';
import type { Content, ContentStyles } from '@/entities/content/api/types';
import { VideoPlayerWrapper } from '@/pages/publisher-video-content/VideoPlayerWrapper';
import { Tooltip } from '@/shared';
import { publisherRoutes, useAppDispatch } from '@/shared/lib';
import { toast } from '@/shared/lib/toastManager';
import { stopSubmition } from '@/shared/lib/utils/stopPropagation';
import Button from '@/shared/ui/button';
import PageLoader from '@/shared/ui/PageLoader';
import {
  AnimationStyleSettings,
  ColorsSettings,
  ControlsSettings,
  LinkButtonSettings,
  LogoSettings,
  TextSettings,
  TextStyleAndPositionSettings,
} from '@/widgets/content-styles';

import { videoContentDefaults } from '../lib/defaults';
import {
  VideoContentIn,
  VideoContentPublicationSchema,
  VideoContentSchema,
} from '../lib/schema';

import MetadataSettings from './MetadataSettings';
import PresetSelector from './PresetSelector';
import RenderConfirmationModal from './RenderConfirmationModal';
import Sidebar from './Sidebar';
import TextBlocks from './TextBlocks';
import VideoEditor, { VideoEditorRef } from './VideoEditor';

const AUTOSAVE_INTERVAL = 15_000;

const getErrorMessage = (
  error: FetchBaseQueryError | SerializedError | undefined
): string => {
  if (!error) return 'Something went wrong. Please try again.';

  if ('data' in error) {
    const data = error.data as { message?: string } | undefined;
    if (data?.message) return data.message;
  }

  if ('message' in error && error.message) {
    return error.message;
  }

  return 'Something went wrong. Please try again.';
};

type Props = {
  onClose: OnCloseType;
};

function VideoContentForm(props: Props) {
  const [_forceUpdate, forceUpdate] = useState(false);
  const queryParams = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const form = useForm({
    resolver: zodResolver(VideoContentSchema),
    defaultValues: videoContentDefaults,
  });

  const mode = queryParams.id === 'new' ? 'create' : 'update';

  const contentQuery = useGetVideoContentQuery(queryParams.id!, {
    skip: mode === 'create',
  });

  useEffect(() => {
    if (mode === 'update' && contentQuery.isSuccess) {
      form.reset(contentQuery.data, {
        // TODO: fix bug when we create content
        // keepValues: true,
      });
    }
  }, [mode, contentQuery.isSuccess]);

  useEffect(() => {
    if (contentQuery.isError) {
      console.error("Can't load content with id:", queryParams.id);
      console.error({ error: contentQuery.error });
      navigate(publisherRoutes.content);
    }
  }, [contentQuery.isError]);

  const [createContent, createContentMutation] =
    useCreateVideoContentMutation();
  const [updateContent, updateContentMutation] =
    useUpdateVideoContentMutation();
  const [publishContent, publishContentMutation] = usePublishContentMutation();
  const [unpublishContent, unpublishContentMutation] =
    useUnpublishContentMutation();

  const videoEditorRef = useRef<VideoEditorRef>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  async function handleSave(params?: {
    withPublish?: boolean;
    fromAutosave?: boolean;
  }) {
    if (createContentMutation.isLoading || updateContentMutation.isLoading) {
      console.log('already saving');
      return;
    }

    if (mode === 'create') {
      console.log('create draft');

      await form.handleSubmit(
        async (formValues) => {
          const createdContent = await createContent({
            ...formValues,
            editor_config: JSON.stringify(videoEditorRef.current?.serialize()),
            status: 0,
          }).unwrap();

          const content = createdContent.data;
          await dispatch(
            contentApi.util.upsertQueryData(
              'getVideoContent',
              `${content.id}`,
              content
            )
          );

          navigate(`/publisher/content/video/${content.id}`);
          form.setValue('id', content.id);
        },
        (formErrors) => {
          console.log({ formErrors });
        }
      )();
    } else if (mode === 'update') {
      if (form.getValues('status') === 1) {
        // @ts-expect-error
        // eslint-disable-next-line
        form.control._options.resolver = zodResolver(
          VideoContentPublicationSchema
        );

        await form.handleSubmit(
          async (formValues) => {
            await updateContent({
              ...formValues,
              editor_config: JSON.stringify(
                videoEditorRef.current?.serialize()
              ),
              video: params?.fromAutosave ? undefined : renderedVideo,
              id: queryParams.id,
            }).unwrap();
          },
          (formErrors) => {
            console.log({ formErrors });
          }
        )();
      } else {
        await form.handleSubmit(
          async (formValues) => {
            console.log('Save draft');
            await updateContent({
              ...formValues,
              editor_config: JSON.stringify(
                videoEditorRef.current?.serialize()
              ),
              video: params?.fromAutosave ? undefined : renderedVideo,
              id: queryParams.id,
            }).unwrap();
          },
          (formErrors) => {
            console.log({ formErrors });
          }
        )();
      }
    }

    if (params?.withPublish) {
      console.log('Publish after save');
      // @ts-expect-error
      // eslint-disable-next-line
      form.control._options.resolver = zodResolver(
        VideoContentPublicationSchema
      );

      await form.handleSubmit(
        async () => {
          console.log('Publish', form.getValues('id'));
          if (form.getValues('id')) {
            await publishContent(form.getValues('id')).unwrap();
            form.setValue('status', 1);
          }
        },
        (formErrors) => {
          console.log("Can't publish", form.getValues('id'), { formErrors });
        }
      )();
    }
  }

  // AUTOSAVE:
  const prevValuesRef = useRef<VideoContentIn>();
  useEffect(() => {
    const id = window.setInterval(() => {
      if (createContentMutation.isError || updateContentMutation.isError)
        return;

      const current = form.getValues();
      if (current.status === 1) return;

      const prev = prevValuesRef.current;

      if (prev !== undefined && !isEqual(prev, current)) {
        console.log('autosave:', current);
        handleSave({ fromAutosave: true });
      }

      prevValuesRef.current = cloneDeep(current);
    }, AUTOSAVE_INTERVAL);

    return () => window.clearInterval(id);
  }, [form, handleSave]);

  const [renderedVideo, setRenderedVideo] = useState<Blob>();
  const renderedVideoUrl = useMemo<string | undefined>(
    () => (renderedVideo ? URL.createObjectURL(renderedVideo) : undefined),
    [renderedVideo]
  );

  const [showRenderConfirmationModal, setShowRenderConfirmationModal] =
    useState(false);

  const [lastRenderedTimeline, setLastRenderedTimeline] = useState<object>();
  const currentTimeline = videoEditorRef.current?.serialize()?.timeline;

  const hasJustRendered =
    !!lastRenderedTimeline && isEqual(lastRenderedTimeline, currentTimeline);

  const onSidebarToggleChange = (open: boolean) => {
    setIsSidebarOpen(open);
  };

  const onSidebarTabSelectionChange = (tabIdx: number) => {
    if (tabIdx >= 0 && tabIdx <= 2) {
      setSelectedTabIndex(tabIdx);
    } else if (tabIdx === 3) {
      setShowRenderConfirmationModal(true);
    }
  };

  async function renderVideo() {
    if (!videoEditorRef.current) return;
    if (hasJustRendered) {
      setShowRenderConfirmationModal(false);
      toast('The preview is already up to date with your edits.');
      return;
    }

    setShowRenderConfirmationModal(false);

    if (renderedVideoUrl) URL.revokeObjectURL(renderedVideoUrl);

    const exportResult = await videoEditorRef.current?.export();
    if (!exportResult) return;

    setRenderedVideo(exportResult.blob);

    const editorConfig = videoEditorRef.current.serialize();
    if (!editorConfig) return;

    setLastRenderedTimeline(editorConfig.timeline);
  }

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedPresetId, setSelectedPresetId] =
    useState<ContentStyles['id']>();

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const containerMaxWidth = `calc(100vw - ${isSidebarOpen ? '8' : '4.5'}rem)`;

  const isSaveButtonLoading =
    (mode === 'create' && createContentMutation.isLoading) ||
    (mode === 'update' && updateContentMutation.isLoading);
  // we dont check if video present here, because it couldn't be published without it
  const shouldRenderBeforeSave =
    form.getValues('status') === 1 && !hasJustRendered;
  const shouldBeRenderedBeforePublish =
    form.getValues('status') === 0 && (!renderedVideo || !hasJustRendered);

  const isPublishButtonLoading =
    form.getValues('status') === 0
      ? publishContentMutation.isLoading
      : unpublishContentMutation.isLoading;

  if (contentQuery.isLoading) return <PageLoader />;

  return (
    <>
      <div
        className="!max-w-screen flex h-auto min-h-full flex-row"
        style={{ maxWidth: '100vw !important' }}
      >
        <Sidebar
          mode={mode === 'create' ? 'create' : 'edit'}
          type="video"
          open
          selectedTab={selectedTabIndex}
          setSelectedTab={onSidebarTabSelectionChange}
          onToggleChange={onSidebarToggleChange}
          disabled={false}
          videoConfigurationDisabled={false}
          renderButtonDisabled={
            !currentTimeline?.layers || currentTimeline?.layers?.length === 0
          }
        />

        <div
          className="flex grow flex-col bg-paper"
          style={{ maxWidth: containerMaxWidth }}
        >
          <form onSubmit={stopSubmition}>
            <div className="flex min-h-[68px] items-center gap-2 px-8 py-4 align-middle">
              <div className="flex grow text-h4 font-semibold">
                {mode === 'update' ? 'Edit' : 'Create'} video
              </div>

              <div className="flex max-w-[50%]">
                {createContentMutation.isError && (
                  <div className="text-sm font-light text-red">
                    {getErrorMessage(createContentMutation.error)}
                  </div>
                )}

                {updateContentMutation.isError && (
                  <div className="text-sm font-light text-red">
                    {getErrorMessage(updateContentMutation.error)}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4">
                <Button
                  variant="secondary"
                  title="Close"
                  size="sm"
                  onClick={(e) => {
                    form.reset();
                    if (mode === 'update' && queryParams.id) {
                      dispatch(
                        contentApi.util.invalidateTags([
                          { type: 'ContentList', id: queryParams.id },
                        ])
                      );
                    }
                    props.onClose('onCloseClick', e);
                  }}
                />

                <div className="flex flex-row gap-1 rounded-lg bg-white p-1">
                  <Tooltip
                    title="This project changed after it was last published. Render again before saving."
                    disabled={!shouldRenderBeforeSave}
                  >
                    <Button
                      variant={
                        form.getValues('status') === 0 ? 'secondary' : 'primary'
                      }
                      title="Save"
                      size="sm"
                      className={twMerge(
                        'disabled:cursor-not-allowed',
                        form.getValues('status') === 0
                          ? ''
                          : 'disabled:!bg-gray'
                      )}
                      isLoading={isSaveButtonLoading}
                      disabled={shouldRenderBeforeSave || isSaveButtonLoading}
                      onClick={() => {
                        handleSave();
                      }}
                    />
                  </Tooltip>

                  <Tooltip
                    title="Render the project before publishing."
                    disabled={!shouldBeRenderedBeforePublish}
                  >
                    <Button
                      title={
                        form.getValues('status') === 0 ? 'Publish' : 'Unpublish'
                      }
                      variant={
                        form.getValues('status') === 0 ? 'primary' : 'secondary'
                      }
                      size="sm"
                      isLoading={isPublishButtonLoading}
                      disabled={
                        shouldBeRenderedBeforePublish ||
                        isPublishButtonLoading ||
                        createContentMutation.isLoading ||
                        updateContentMutation.isLoading
                      }
                      className={twMerge(
                        'disabled:cursor-not-allowed',
                        form.getValues('status') === 0
                          ? 'disabled:!bg-gray'
                          : ''
                      )}
                      onClick={async () => {
                        if (form.getValues('status') === 0) {
                          handleSave({ withPublish: true });
                        } else if (queryParams.id) {
                          await unpublishContent(queryParams.id).unwrap();

                          form.clearErrors();
                          form.setValue('status', 0);
                        }
                      }}
                    />
                  </Tooltip>
                </div>
              </div>
            </div>

            <FormProvider {...form}>
              <div className="flex grow flex-col gap-4 px-8 pb-4">
                <div
                  className={twMerge(
                    'flex grow flex-col gap-4',
                    selectedTabIndex === 0 ? '' : 'hidden'
                  )}
                >
                  <MetadataSettings />
                  <div
                    className={twMerge(
                      'w-full justify-center text-red',
                      form.formState.errors?.editor_config?.message
                        ? 'flex'
                        : 'hidden'
                    )}
                  >
                    {form.formState.errors?.editor_config?.message}
                  </div>
                  <VideoEditor
                    ref={videoEditorRef}
                    onClipChange={() => {
                      forceUpdate((value) => !value);
                      form.setValue(
                        'editor_config',
                        JSON.stringify(videoEditorRef.current?.serialize())
                      );
                    }}
                    onLibraryChange={() => {
                      form.setValue(
                        'editor_config',
                        JSON.stringify(videoEditorRef.current?.serialize())
                      );
                    }}
                  />
                </div>

                <div
                  className={twMerge(
                    'relative flex gap-4 rounded-lg bg-white p-4 dark:bg-default',
                    selectedTabIndex === 1 ? '' : 'hidden'
                  )}
                >
                  <div className="flex !w-[348px] !min-w-[348px] !max-w-[348px] flex-col gap-6">
                    <PresetSelector
                      selectedPresetId={selectedPresetId}
                      setSelectedPresetId={setSelectedPresetId}
                    />
                    <ColorsSettings />
                    <ControlsSettings />
                    <LogoSettings />
                  </div>
                  <div className="flex w-full items-start justify-center overflow-hidden">
                    <VideoPlayerWrapper
                      renderedVideoURL={renderedVideoUrl}
                      rendleyEngine={
                        videoEditorRef.current?.getEngine() || undefined
                      }
                      rendleyCanvas={
                        videoEditorRef.current?.getCanvas() || undefined
                      }
                    />
                  </div>
                </div>

                <div
                  className={twMerge(
                    'relative flex gap-4 rounded-lg bg-white p-4 dark:bg-default',
                    selectedTabIndex === 2 ? '' : 'hidden'
                  )}
                >
                  <div className="flex !w-[348px] !min-w-[348px] !max-w-[348px] flex-col gap-6">
                    <PresetSelector
                      selectedPresetId={selectedPresetId}
                      setSelectedPresetId={setSelectedPresetId}
                    />
                    <LinkButtonSettings />
                    <TextSettings />
                    <TextStyleAndPositionSettings />
                    <AnimationStyleSettings />
                  </div>
                  <div className="flex w-full flex-col gap-4 overflow-hidden">
                    <div className="flex w-full items-start justify-center overflow-hidden">
                      <VideoPlayerWrapper
                        renderedVideoURL={renderedVideoUrl}
                        rendleyEngine={
                          videoEditorRef.current?.getEngine() || undefined
                        }
                        rendleyCanvas={
                          videoEditorRef.current?.getCanvas() || undefined
                        }
                      />
                    </div>
                    <TextBlocks />
                  </div>
                </div>
              </div>
            </FormProvider>
          </form>
        </div>
      </div>

      <RenderConfirmationModal
        visible={showRenderConfirmationModal}
        renderVideo={() => renderVideo()}
        onClose={() => setShowRenderConfirmationModal(false)}
      />
    </>
  );
}

export default VideoContentForm;
